<template>
  <section :data-e2e="TaxCypressAttribute.finalTaxesCalculator.views.CALCULATOR">
    <FinalTaxesDescriptionContent @scroll-to-calculator="scrollToCalculator" />
    <ValidationObserver v-slot="{ invalid }">
      <section ref="calculator">
        <section class="is-justify-content-center columns">
          <FinalTaxesJurisdictionSelector v-model="jurisdictionId" />
        </section>
        <section class="is-justify-content-center columns">
          <div class="column is-6">
            <section>
              <p class="is-size-2 has-text-weight-semibold is-text-align-center mb-3">
                {{ $t('tax.final-taxes-calculator-heading') }}
              </p>
              <p class="is-size-5">
                {{ $t('tax.final-taxes-calculator-subheading') }}
              </p>
            </section>
            <FinalTaxesAssetForm v-model="assets" />
            <FinalTaxesOtherIncomeInput v-model="otherIncome" />
            <div class="columns is-justify-content-center my-5">
              <div class="column is-7">
                <ButtonView
                  class="button m-b-20"
                  :label="$t('tax.calculate')"
                  type="is-primary"
                  :expanded="true"
                  :disabled="invalid"
                  @click="calculateFinalTaxes"
                  :data-e2e="TaxCypressAttribute.finalTaxesCalculator.buttons.CALCULATE"
                />
              </div>
            </div>
            <FinalTaxesResult class="m-b-20" :has-calculated="hasCalculated" @download="download">
              <template #final>
                {{ animatedEstimatedFinalTaxes.formattedAmount }}
              </template>
              <template #spouse>
                {{ animatedPayableAtSpouseDeath.formattedAmount }}
              </template>
            </FinalTaxesResult>
            <FinalTaxesBreakDown v-if="hasCalculated" :line-items="finalTaxesData.lineItems" />
          </div>
        </section>
        <LoadingView :active="isLoading" />
      </section>
    </ValidationObserver>
  </section>
</template>

<script>
import ValidationObserver from '/@/modules/core/form/components/ValidationObserver.vue';
import { TaxCypressAttribute } from '@/modules/tax/constants/TaxCypressAttribute';
import FinalTaxesDescriptionContent from '@/modules/tax/components/presentational/finaltaxescalculator/FinalTaxesDescriptionContent.vue';
import FinalTaxesJurisdictionSelector from '@/modules/tax/components/presentational/finaltaxescalculator/FinalTaxesJurisdictionSelector.vue';
import { FinalTaxesAssetType } from '@/modules/tax/enums/FinalTaxesAssetType';
import FinalTaxesOtherIncomeInput from '@/modules/tax/components/stateful/finaltaxescalculator/FinalTaxesOtherIncomeInput.vue';
import FinalTaxesAssetForm from '@/modules/tax/components/presentational/finaltaxescalculator/FinalTaxesAssetForm.vue';
import FinalTaxesBreakDown from '@/modules/tax/components/presentational/finaltaxescalculator/FinalTaxesBreakDown.vue';
import FinalTaxesResult from '@/modules/tax/components/presentational/finaltaxescalculator/FinalTaxesResult.vue';
import { PredefinedJurisdiction } from '@/modules/predefined/enums/PredefinedJurisdiction';
import TaxCalculatorService from '@/modules/tax/services/CalculatorService';
import { CalculatorType } from '@/modules/tax/constants/CalculatorType';
import { HasCalculatorMixin } from '@/modules/tax/mixins/HasCalculatorMixin';
import { AnimatedTaxAmount } from '@/modules/tax/models/calculators/AnimatedTaxAmount';
import { ExportType } from '@/modules/export/constants/ExportType';
import { DownloadToPdfMixin } from '@/modules/export/mixins/DownloadToPdfMixin';
import { ExportViewerServiceFactory } from '@/modules/export/services/ExportViewerServiceFactory';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';
import LoadingView from '@/modules/core/ui/components/wrappers/LoadingView.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'FinalTaxesCalculatorView',
  mixins: [HasCalculatorMixin, DownloadToPdfMixin],
  components: {
    ButtonView,
    FinalTaxesBreakDown,
    FinalTaxesAssetForm,
    FinalTaxesResult,
    FinalTaxesOtherIncomeInput,
    FinalTaxesJurisdictionSelector,
    FinalTaxesDescriptionContent,
    ValidationObserver,
    LoadingView
  },
  static: {
    TaxCypressAttribute,
    FinalTaxesAssetType
  },
  local() {
    return {
      type: ExportType.FINAL_TAXES_SUMMARY,
      resourceId: null
    };
  },
  data() {
    return {
      isLoading: false,
      jurisdictionId: PredefinedJurisdiction.ONTARIO.id,
      finalTaxesData: null,
      assets: {
        [FinalTaxesAssetType.REAL_ESTATE.dataKey]: [],
        [FinalTaxesAssetType.BANK_ACCOUNT.dataKey]: [],
        [FinalTaxesAssetType.OTHER_INVESTMENT.dataKey]: [],
        [FinalTaxesAssetType.BUSINESS_INTEREST.dataKey]: [],
        [FinalTaxesAssetType.ART_JEWELRY.dataKey]: []
      },
      otherIncome: 0,
      animatedEstimatedFinalTaxes: new AnimatedTaxAmount({}),
      animatedPayableAtSpouseDeath: new AnimatedTaxAmount({})
    };
  },
  computed: {
    hasCalculated() {
      return !!this.finalTaxesData;
    }
  },
  methods: {
    scrollToCalculator() {
      this.$refs.calculator?.scrollIntoView({ behavior: 'smooth' });
    },
    async calculateFinalTaxes() {
      this.isLoading = true;
      const params = {
        jurisdictionId: this.jurisdictionId,
        type: CalculatorType.FINAL_TAXES,
        inputs: { assets: this.assets, otherIncome: this.otherIncome }
      };
      try {
        this.finalTaxesData = await TaxCalculatorService.calculate(params);
      } finally {
        this.isLoading = false;
      }

      this.animateFinalTaxes();
    },
    animateFinalTaxes() {
      this.animatedEstimatedFinalTaxes.target = Number(
        this.finalTaxesData?.calculations?.estimatedFinalTaxes
      );
      this.animatedPayableAtSpouseDeath.target = Number(
        this.finalTaxesData?.calculations?.payableAtSpouseDeath
      );

      this.animateAmount(this.animatedEstimatedFinalTaxes);
      this.animateAmount(this.animatedPayableAtSpouseDeath);
    },
    async download() {
      try {
        this.isLoading = true;
        const cacheItem = await TaxCalculatorService.saveDataInCache({
          ...this.finalTaxesData,
          exportType: ExportType.FINAL_TAXES_SUMMARY
        });
        this.service = ExportViewerServiceFactory.build(this.type, cacheItem.cacheKey, {});
        await this.onDownload();
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
:deep(.form-multi-select),
:deep(.form-input) {
  margin: 0 !important;
}

:deep(.b-checkbox.checkbox) {
  align-items: unset;
}

:deep(.b-checkbox.checkbox .check) {
  margin-top: var(--ce-2px);
}

:deep(.box.box-button.selected) {
  background-color: var(--ce-white);
}

:deep(.table-wrapper) {
  min-height: var(--ce-100px) !important;
}
</style>
